import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import AddressField from '@/components/form/fields/address/AddressField';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { serialiseAddress } from '@/pages/onboarding/commonSteps/addressHelper';
import { Address } from '@/services/api/commonTypes';
import { ApprovalStatus } from '@/services/api/contracts/companies';
import { useCreateCompany, useCreateCompanyCode, useUpdateCompany } from '@/services/api/hooks/companies';
import { useInviteToCompany } from '@/services/api/hooks/invites';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, Suspense, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import EmployeeCountField from '../EmployeeCountField';

const CreateCustomerFormFields = z.object({
  companyInfo: z.object({
    name: z.string().min(1, 'Required'),
    number: z.string().min(1, 'Required'),
    address: Address,
    employeeCount: z.number().min(1),
    vatNumber: z.string().min(1, 'Required'),
    isProductToursEnabled: z.boolean().default(false),
    addHaloStockSites: z.string().optional().nullable()
  })
});

type CreateCustomerFormFields = z.infer<typeof CreateCustomerFormFields>;

const CreateCustomerModal: FunctionComponent<ModalProps<{ mspId: string }>> = props => {
  const CreateCustomer = () => {
    const { role } = useUser();

    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing partner id');

    const methods = useForm<CreateCustomerFormFields>({
      resolver: zodResolver(CreateCustomerFormFields),
      defaultValues: {
        companyInfo: { employeeCount: 1, isProductToursEnabled: false }
      }
    });

    const createCustomer = useCreateCompany(mspId);
    const updateCustomer = useUpdateCompany(mspId);
    const createCustomerCode = useCreateCompanyCode();
    const invite = useInviteToCompany();

    const [loading, setLoading] = useState(false);

    const onSubmit = async (data: CreateCustomerFormFields) => {
      setLoading(true);
      const createCustomerCodeResult = await createCustomerCode.mutateAsync({ names: [data.companyInfo.name] });

      const result = await createCustomer.mutateAsync({
        name: data.companyInfo.name,
        code: createCustomerCodeResult[0].code,
        registeredInfo: {
          name: data.companyInfo.name,
          number: data.companyInfo.number,
          address: serialiseAddress(data.companyInfo.address),
          employeeCount: data.companyInfo.employeeCount,
          vatNumber: data.companyInfo.vatNumber
        }
      });

      // Requirement to set approval status to full post creation.
      await updateCustomer.mutateAsync({
        name: result.name,
        code: result.code,
        id: result.id,
        approvalStatus: ApprovalStatus.Full,
        disabled: false,
        registeredInfo: result.registeredInfo,
        selectedProductIds: [],
        enableCustomNotifications: false,
        isProductToursEnabled: data.companyInfo.isProductToursEnabled,
        addHaloStockSites: data.companyInfo.addHaloStockSites ? data.companyInfo.addHaloStockSites.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : null
      });

      setLoading(false);
      props.close();
    };

    const getError = () => {
      const createCustomerErrorObj = createCustomer.error as Error | undefined;
      const createCustomerCodeErrorObj = createCustomerCode.error as Error | undefined;
      const inviteErrorObj = invite.error as Error | undefined;

      return createCustomerCodeErrorObj
        ? createCustomerCodeErrorObj.message
        : createCustomerErrorObj
        ? createCustomerErrorObj.message
        : inviteErrorObj
        ? inviteErrorObj.message
        : '';
    };

    const error = getError();

    return (
      <Form className="flex w-full flex-col space-y-3 py-3" methods={methods} onSubmit={onSubmit}>
        <div>
          <div className="flex flex-row space-x-5">
            <TextField className="flex-1" id="companyInfo.name" label="Company name" />
            <TextField id="companyInfo.number" label="Registered company number" />
          </div>
          <TextField id="companyInfo.vatNumber" label="VAT number" />
          <AddressField fieldKey="companyInfo" />
          <EmployeeCountField />
          {role.type === RoleType.Admin && (
            <>
              <div className="flex items-center gap-3 py-4">
                <p className="text-sm font-medium text-gray-700">Enable Product Tours</p>
                <Controller
                  name="companyInfo.isProductToursEnabled"
                  control={methods.control}
                  render={({ field }) => (
                    <ToggleInput id="companyInfo.isProductToursEnabled" value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex flex-row space-x-5 py-2">
                <TextField className="flex-1" id="companyInfo.addHaloStockSites" label="Halo Stock Sites (comma-separated IDs)" />
              </div>
            </>
          )}
        </div>

        {error && <p className="text-red-500">{error}</p>}
        <div className="flex justify-end pt-2">
          <Button loading={loading} className="w-fit" variant={ButtonVariant.Primary} type="submit">
            Create
          </Button>
        </div>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Create new customer</h1>
        </Modal.Controls>
        <Suspense fallback={<Loading />}>
          <CreateCustomer />
        </Suspense>
      </Card>
    </Modal>
  );
};

export default CreateCustomerModal;
